import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Space, Switch, Typography, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { OperationalActionDescriptor } from '../../routes/execution/executionActionsFacade';
import type { ExecutionCargoHydration } from '../types';
import type { DispatchOrchestration } from './dispatchOrchestration';
import ExecutionActionModal, {
  type ExecutionActionSubmitPayload,
  resolveActionSequence
} from '../ExecutionActionModal';
import {
  getExecutionWorkflowMode,
  setExecutionWorkflowMode
} from '../../routes/execution/workflowModeFacade';
import type { ExecutionIncidentType } from '../../routes/execution/incidentFacade';

const { Text } = Typography;

type Props = {
  routeId: number;
  orchestration: DispatchOrchestration;
  cargoAtStop: ExecutionCargoHydration[];
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  onOpenCompletion?: () => void;
  onResolveIncident?: (incidentId: number) => void;
  onModeChange?: () => void;
  loading?: boolean;
  incidentReportRequest?: ExecutionIncidentType | null;
  onClearIncidentRequest?: () => void;
};

export const DispatchActionSurface: React.FC<Props> = ({
  routeId,
  orchestration,
  cargoAtStop,
  onSubmit,
  onOpenCompletion,
  onResolveIncident,
  onModeChange,
  loading = false,
  incidentReportRequest = null,
  onClearIncidentRequest
}) => {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [activeDescriptor, setActiveDescriptor] = useState<OperationalActionDescriptor | null>(null);
  const [advancedMode, setAdvancedMode] = useState(getExecutionWorkflowMode() === 'advanced');
  const [presetIncidentType, setPresetIncidentType] = useState<ExecutionIncidentType | undefined>();

  const { primary, secondaryActions } = orchestration;

  useEffect(() => {
    if (!incidentReportRequest) return;
    const report = secondaryActions.find((a) => a.opensIncident);
    if (report) {
      setPresetIncidentType(incidentReportRequest);
      setActiveDescriptor(report);
    }
    onClearIncidentRequest?.();
  }, [incidentReportRequest, secondaryActions, onClearIncidentRequest]);

  const runDescriptor = async (descriptor: OperationalActionDescriptor) => {
    if (descriptor.disabled) return;
    if (descriptor.key === 'complete_route' && onOpenCompletion) {
      onOpenCompletion();
      return;
    }
    setPresetIncidentType(undefined);
    setActiveDescriptor(descriptor);
  };

  const handlePrimaryClick = async () => {
    if (primary.incidentId != null && onResolveIncident) {
      onResolveIncident(primary.incidentId);
      return;
    }
    if (primary.opensCompletion && onOpenCompletion) {
      onOpenCompletion();
      return;
    }
    if (primary.descriptor) {
      await runDescriptor(primary.descriptor);
    }
  };

  const handleModalSubmit = async (payload: ExecutionActionSubmitPayload) => {
    const key = `${payload.descriptor.key}:${payload.descriptor.executionStopUid ?? 'route'}`;
    setPendingKey(key);
    try {
      await onSubmit(payload);
      message.success(`Выполнено: ${payload.descriptor.label}`);
      setActiveDescriptor(null);
    } catch (e) {
      const err = e as { data?: { error?: string }; message?: string };
      message.error(err?.data?.error ?? err?.message ?? 'Не удалось выполнить операцию');
    } finally {
      setPendingKey(null);
    }
  };

  const secondaryMenuItems = secondaryActions
    .filter((a) => !a.disabled || a.key === 'complete_route')
    .map((descriptor) => {
      const key = `${descriptor.key}:${descriptor.executionStopUid ?? 'route'}`;
      return {
        key,
        label: descriptor.label,
        danger: descriptor.opensIncident,
        disabled:
          descriptor.disabled &&
          descriptor.key !== 'complete_route' &&
          resolveActionSequence(descriptor).length === 0,
        onClick: () => void runDescriptor(descriptor)
      };
    });

  const openDowntimeIncident = () => {
    const report = secondaryActions.find((a) => a.opensIncident);
    if (!report) return;
    setPresetIncidentType('downtime');
    setActiveDescriptor(report);
  };

  return (
    <div className="execution-workspace__actions">
      <Space style={{ marginBottom: 8 }} size={4}>
        <Text className="execution-workspace__muted">Режим</Text>
        <Switch
          size="small"
          checked={advancedMode}
          checkedChildren="Расш."
          unCheckedChildren="Простой"
          onChange={(checked) => {
            setAdvancedMode(checked);
            setExecutionWorkflowMode(checked ? 'advanced' : 'simple');
            onModeChange?.();
          }}
        />
      </Space>

      <Button
        type="primary"
        size="large"
        block
        className="execution-workspace__primary-action"
        danger={primary.priority === 'critical'}
        loading={loading && pendingKey != null}
        onClick={() => void handlePrimaryClick()}
      >
        {primary.label}
      </Button>

      {secondaryMenuItems.length > 0 ? (
        <Dropdown
          menu={{
            items: [
              ...secondaryMenuItems,
              { type: 'divider' },
              {
                key: 'downtime',
                label: 'Зафиксировать простой',
                onClick: openDowntimeIncident
              }
            ]
          }}
          trigger={['click']}
        >
          <Button type="text" size="small" block icon={<DownOutlined />}>
            Другие действия
          </Button>
        </Dropdown>
      ) : null}

      <ExecutionActionModal
        open={activeDescriptor != null}
        descriptor={activeDescriptor}
        routeId={routeId}
        cargoAtStop={cargoAtStop}
        loading={loading}
        defaultIncidentType={presetIncidentType}
        onCancel={() => {
          setActiveDescriptor(null);
          setPresetIncidentType(undefined);
        }}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};

export default DispatchActionSurface;
